import { ArrowRight, ArrowUpRight, Briefcase, Globe2, Heart, Sparkles, Users } from "lucide-react";

import AppButton from "@/components/ui-system/AppButton";
import Breadcrumb from "@/components/Breadcrumb";
import { IMG } from "@/data/imageLibrary";
import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import AnimatedCounter from "@/components/motion/AnimatedCounter";
import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

export default function CareersPage({ lang }) {
  const isJa = lang === "ja";

  const values = isJa
    ? [
        { icon: Users, title: "現場から考える", body: "お客様の実際の課題を観察し、優先順位を決めるところから始めます。" },
        { icon: Sparkles, title: "シンプルさを選ぶ", body: "派手な技術より、長く運用できる、安全でシンプルな仕組みを選びます。" },
        { icon: Globe2, title: "バイリンガル運営", body: "日本語と英語の両方で、お客様、社内、ドキュメントを運用します。" },
        { icon: Heart, title: "お客様への誠実さ", body: "短期の数字より、長期の信頼を優先します。" },
      ]
    : [
        { icon: Users, title: "Start from the ground", body: "Observe customer problems first. Decide priorities second." },
        { icon: Sparkles, title: "Choose simplicity", body: "Prefer simple, safe, long-lasting systems over flashy stacks." },
        { icon: Globe2, title: "Bilingual by default", body: "Customers, internal teams and documents all operate in JA and EN." },
        { icon: Heart, title: "Honest with customers", body: "Long-term trust beats short-term numbers." },
      ];

  return (
    <article data-testid="careers-page" className="bg-white">
      <SEO {...PAGE_SEO.careers[lang]} path={`/${lang}/careers`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "採用情報" : "Careers", url: `/${lang}/careers` },
        ])}
      />
      <Breadcrumb lang={lang} crumbs={[{ label: isJa ? "採用情報" : "Careers" }]} />

      {/* Hero */}
      <section className="relative w-full bg-[#5B2E83] overflow-hidden" style={{ minHeight: "320px" }}>
        <img src={IMG.about} alt={isJa ? "NoJo Enterprise横須賀本社オフィス — 神奈川県" : "NoJo Enterprise Yokosuka office — Kanagawa, Japan"} className="absolute inset-0 w-full h-full object-cover opacity-50" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#5B2E83]/85 via-[#4A2570]/60 to-transparent" />

        {/* ANIM 1: mobility flow line over hero top-right */}
        <BrandGraphic variant="mobility" color="#7AB8E8" className="absolute top-4 right-0 w-[55%] max-w-[640px] h-28 opacity-65 pointer-events-none hidden md:block" data-testid="careers-hero-graphic" />
        {/* ANIM 2: orbit decoration */}
        <BrandGraphic variant="orbit" color="#7AB8E8" className="hidden lg:block absolute -bottom-16 -right-12 w-60 h-60 opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="max-w-2xl text-white animate-brand-fade-up">
            <div className="inline-flex items-center gap-2 mb-3">
              <Briefcase className="h-4 w-4 text-[#7AB8E8]" strokeWidth={1.75} />
              <span className="text-[10px] tracking-[0.3em] font-mono-display uppercase font-bold text-[#A8C8E5]">
                {isJa ? "採用情報" : "Careers"}
              </span>
            </div>
            <h1 className={[isJa ? "font-ja" : "font-display", "text-white text-4xl md:text-5xl lg:text-[54px] tracking-tighter font-bold leading-[1.05]"].join(" ")}>
              {isJa ? "横須賀から、日本の実務課題を変える仕事を。" : "Work that changes Japan's everyday operations, from Yokosuka."}
            </h1>
            <p className="mt-4 text-[15px] md:text-[16px] leading-[1.65] text-[#E2E8F0] max-w-xl">
              {isJa
                ? "NoJo Enterpriseは、現場のお客様課題から実用的なデジタルサービスを生み出すチームです。今後、エンジニア、運用、サポート、コンテンツの各領域で採用を予定しています。"
                : "NoJo Enterprise is a small team turning ground-level customer pain points into practical digital services. We expect to open roles across engineering, operations, customer support and content."}
            </p>
            <p className="mt-3 text-[13px] leading-relaxed text-[#A8C8E5] max-w-xl">
              {isJa
                ? "現在、公開されている募集はありません。ご興味のある方は、お問い合わせフォームよりご連絡ください。"
                : "We don't have public openings listed at the moment. If you'd like to be considered, please reach out via the contact form."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <AppButton
                to={`/${lang}/contact`}
                variant="blue"
                trailingIcon={ArrowRight}
                data-testid="careers-cta-contact"
              >
                {isJa ? "応募・お問い合わせ" : "Get in touch"}
              </AppButton>
            </div>
          </div>
        </div>
      </section>

      {/* ANIM 3: count-up trust strip between hero and values */}
      <section className="bg-white border-y border-[#E2E8F0]" data-testid="careers-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: 13, suffix: isJa ? "年" : " yrs", label: isJa ? "横須賀から運営" : "Operating from Yokosuka", color: "#5B2E83" },
            { value: 2, suffix: "", label: isJa ? "運営中のサービス" : "Services live", color: "#2F8F3C" },
            { value: 1, suffix: "", label: isJa ? "準備中のサービス" : "Service in development", color: "#0E5FB0" },
            { value: 2, suffix: "", label: isJa ? "対応言語 (JA / EN)" : "Languages (JA / EN)", color: "#0F1D2D" },
          ].map((s, i) => (
            <Reveal as="div" variant="up" delay={i * 100} key={i} data-testid={`careers-strip-${i + 1}`} className="border-l-2 pl-4" style={{ borderColor: s.color }}>
              <div className="font-display text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tighter leading-none">
                <AnimatedCounter to={s.value} suffix={s.suffix} duration={1500} />
              </div>
              <div className="mt-2 text-[12px] tracking-tight text-[#475569] leading-snug">
                {s.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] font-bold uppercase">
            {isJa ? "私たちが大切にする働き方" : "How we work"}
          </div>
          <h2 className={["mt-3 mb-10", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-3xl md:text-4xl tracking-tight font-bold leading-tight max-w-2xl"].join(" ")}>
            {isJa ? "4つの基本姿勢。" : "Four operating principles."}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              const colors = ["#5B2E83", "#2F8F3C", "#0E5FB0", "#0F1D2D"];
              const c = colors[i];
              return (
                <Reveal as="li" variant="up" delay={i * 110} key={i} data-testid={`careers-value-${i + 1}`} className="bg-white border border-[#E2E8F0] rounded-md p-6 transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(15,29,45,0.18)]">
                  <span aria-hidden className="inline-flex items-center justify-center h-10 w-10 rounded-md animate-brand-float" style={{ backgroundColor: `${c}15`, animationDelay: `${i * 320}ms` }}>
                    <Icon className="h-5 w-5" strokeWidth={1.6} style={{ color: c }} />
                  </span>
                  <h3 className={["mt-4", isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[16px] font-bold tracking-tight"].join(" ")}>
                    {v.title}
                  </h3>
                  <p className="mt-2.5 text-[13.5px] leading-[1.8] text-[#475569]">
                    {v.body}
                  </p>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h3 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-xl md:text-2xl tracking-tight font-bold max-w-2xl leading-snug"].join(" ")}>
            {isJa
              ? "NoJo Enterpriseに興味を持っていただきありがとうございます。"
              : "Thanks for your interest in NoJo Enterprise."}
          </h3>
          <AppButton to={`/${lang}/contact`} variant="purple" trailingIcon={ArrowUpRight} data-testid="careers-cta-bottom">
            {isJa ? "お問い合わせ" : "Contact us"}
          </AppButton>
        </div>
      </section>
    </article>
  );
}
